// First-party Aldoo Analytics client (port of aldoo-web-app/src/utils/aldoo-analytics.js):
// POST {ANALYTICS_URL}/v1/analytics {session_init} once per session, then buffered
// events ({app_id, deviceID, events:{<session>:[…]}}) flushed on a timer /
// when the buffer is full. Events tracked before init are replayed.
import config from "aldoo-ra/Config"

const LOCAL_KEY = "aalk"
const APP_ID = "aldoo-books"

const state = {
  verbose: false,
  apiUrl: "",
  deviceID: "",
  sessionID: null,
  enabled: false,
  flushRate: 15,
  maxEventBuffer: 100,
  initialized: false,
  initializing: null,
  pending: [],
  buffer: [],
  serverTimeOffset: 0, // server - local (ms)
  getUserID: () => null,
}

const log = (...args) => state.verbose && console.log("[analytics]", ...args)

const analyticsUrl = () => config.ANALYTICS_URL || config.PLATFORM_API_URL || ""

const ep = (name) => `${state.apiUrl.replace(/\/$/, "")}/v1/${name}`

const sanitize = (payload) => {
  const result = {}
  for (const key in payload) {
    const value = payload[key]
    if (value && !(typeof value === "string" && value.length === 0)) result[key] = value
  }
  return result
}

const serverTime = () => new Date(Date.now() + state.serverTimeOffset).toISOString()

function writeBuffer() {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(state.buffer))
  } catch {}
}

function readBuffer() {
  try {
    const data = localStorage.getItem(LOCAL_KEY)
    state.buffer = data ? JSON.parse(data.replace(/\\u003f/g, "")) : []
  } catch {
    state.buffer = []
  }
}

async function post(body) {
  const res = await fetch(ep("analytics"), { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), keepalive: true })
  return res.json()
}

async function flushEvents() {
  if (!state.buffer.length || state.flushing) return
  // the batch leaves the buffer as it is sent: a page that closes meanwhile
  // (the pagehide flush, whose answer never comes) must not hand the same
  // events to the next page to send again — they were counted twice
  const batch = state.buffer
  state.buffer = []
  writeBuffer()
  state.flushing = true
  const payload = { app_id: APP_ID, deviceID: state.deviceID, events: {} }
  for (const event of batch) {
    const { sessionID, ...item } = event
    ;(payload.events[sessionID] ||= []).push(sanitize(item))
  }
  try {
    const response = await post(sanitize(payload))
    // stored is `result: true`; a failed save answers `result: "error"`
    if (response?.result !== true) throw new Error(response?.error || String(response?.result || "not stored"))
  } catch (e) {
    // not taken: back in front of what came since, for the next flush
    state.buffer = [...batch, ...state.buffer]
    writeBuffer()
    log("flush failed", e?.message)
  } finally {
    state.flushing = false
  }
}

/** Initialize once: session_init with the device id (anonymous or signed in). */
export async function initAnalytics({ deviceID, getUserID }) {
  // the landing page's codes, before a client-side navigation leaves them behind
  if (typeof window !== "undefined") visitSource()
  // one `visit` per visit (the tab's session), wherever it lands: a visitor who reads the home page
  // or a listing and leaves counts too (api: checkout-funnel, the funnel by source / campaign)
  if (typeof window !== "undefined") {
    let landed = false
    try {
      landed = !!sessionStorage.getItem(VISIT_KEY)
      sessionStorage.setItem(VISIT_KEY, "1")
    } catch {}
    if (!landed) track("visit", { path: window.location.pathname.slice(0, 200) })
  }
  if (getUserID) state.getUserID = getUserID
  if (state.initialized || state.initializing) return state.initializing
  state.apiUrl = analyticsUrl()
  state.deviceID = deviceID
  if (!deviceID || !state.apiUrl) return log("missing deviceID or ANALYTICS_URL")
  state.initializing = (async () => {
    try {
      const response = await post({ session_init: sanitize({ deviceID, app_id: APP_ID }) })
      if (!response || response.error) return log("init error", response?.error)
      state.sessionID = response.session
      state.flushRate = response.flush_rate || 15
      state.maxEventBuffer = response.max_event_buffer || 100
      state.enabled = !!response.enabled
      if (state.enabled) {
        state.serverTimeOffset = new Date(response.server_time).getTime() - Date.now()
        readBuffer()
        setInterval(flushEvents, state.flushRate * 1000)
        window.addEventListener("pagehide", () => flushEvents())
      }
      state.initialized = true
      const pending = state.pending
      state.pending = []
      for (const { eventID, payload } of pending) track(eventID, payload)
    } catch (e) {
      log("init failed", e?.message)
    } finally {
      state.initializing = null
    }
  })()
  return state.initializing
}

const SOURCE_KEY = "aldoo-visit-source"
const VISIT_KEY = "aldoo-visit-landed"

/**
 * Where this visit came from, worked out once per visit (the tab's session)
 * from the page it landed on: its utm codes, else an ad click id (gclid /
 * gbraid / wbraid: Google; fbclid: Meta), else the site that linked to it —
 * each event carries it (payload.src), so the store's dashboards can count
 * traffic by source, campaign and ad (api: analytics store-dashboards
 * traffic_by_campaign). Nothing personal: the codes and a host name.
 */
function visitSource() {
  try {
    const kept = sessionStorage.getItem(SOURCE_KEY)
    if (kept) return JSON.parse(kept)
  } catch {}
  const params = new URLSearchParams(window.location.search)
  const pick = (key) => (params.get(key) || "").slice(0, 120)
  const src = { source: pick("utm_source"), medium: pick("utm_medium"), campaign: pick("utm_campaign"), content: pick("utm_content"), term: pick("utm_term") }
  if (params.get("gclid") || params.get("gbraid") || params.get("wbraid")) src.click = "google"
  else if (params.get("fbclid")) src.click = "meta"
  let referrer = ""
  try {
    const host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : ""
    if (host && host !== window.location.hostname.replace(/^www\./, "")) referrer = host
  } catch {}
  if (referrer) src.referrer = referrer
  const result = Object.fromEntries(Object.entries(src).filter(([, v]) => v))
  try {
    sessionStorage.setItem(SOURCE_KEY, JSON.stringify(result))
  } catch {}
  return result
}

/** Track an event, e.g. track("PRODUCT_VIEW", { pid }) */
export function track(eventID, payload) {
  if (!eventID || typeof window === "undefined") return
  if (!state.initialized) return state.pending.push({ eventID, payload })
  if (!state.enabled) return
  const src = visitSource()
  const withSource = Object.keys(src).length ? { ...(payload || {}), src } : payload
  state.buffer.push({ sessionID: state.sessionID, eventID: eventID.toLowerCase(), timestamp: serverTime(), payload: withSource, userID: state.getUserID?.() || null })
  writeBuffer()
  if (state.buffer.length >= state.maxEventBuffer) flushEvents()
}


/**
 * The store front reported a purchase to Google Analytics itself: the server
 * then leaves it (its own report, aldoo-com-api order-processing
 * reportPurchase, is for a purchase the browser could not send). Said only
 * when Google's tag really loaded — an ad blocker leaves gtag queueing into
 * nothing, and that purchase is the server's to send.
 */
export function reportPurchaseSent(paymentIntentId) {
  if (!paymentIntentId || typeof window === "undefined") return
  setTimeout(() => {
    const loaded = !!(window.google_tag_manager || window.google_tag_data)
    const url = analyticsUrl()
    if (!loaded || !url) return
    fetch(`${url.replace(/\/$/, "")}/v1/analytics`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ purchase_reported: { paymentIntentId } }),
      keepalive: true,
    }).catch(() => {})
  }, 3000)
}
